/** MV6 page adapter: definitions are prefetched; resolution is synchronous and pre-draw. */
import { FULL_OVERRUN, OVERRUN_FORTUNATE, resolveOverrunContest } from '../janus-engine/movement/overrun-contest.js';
import { CONTEST_ROLL_ID } from '../janus-engine/ability/skill-roll.js';
import { browserRollSource } from '../janus-engine/actions/weapon-attack.js';
import { abilityTruthReaders } from './ability-truth.js';
import { fetchAndCacheObject } from './object-cache.js';
import { actorEncounterFor, actorRowFor, registerOverrunWalkPlanner } from './movement-truth.js';
import { createTriggerConsent, offerFortunate } from './trigger-consent.js';
type Row = Record<string, unknown>;
const row = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const upper = (v: unknown): string => String(Array.isArray(v) ? v[0] : v ?? '').trim().toUpperCase();
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
function definition(encounter: Row, id: string): Row | null {
  // MV9: the encounter catalog can hold a name/type SHELL (no items) — any FULL copy counts; a shell must not mask the prefetched objectCache copy
  // (else `overrun:definition-not-held` forever — same bug class as stealth-walk-truth.ts skillsHeld, MV8 T1–T5).
  for (const d of [row(row(encounter.catalog)?.[upper(id)]), row(window.FateTide?.objectCache?.get(upper(id)))]) if (d && row(d.items)) return d;
  return null;
}
/** MV8 (MV7 Codex r1 P2): the page twin of the recorder's exploration pair window (FT_OVERRUN_PAIR_WINDOW_S = 6 s, whole-second clock → 7 s
 *  here, counted from the ACCEPTED walk, so the page never draws dice the recorder would refuse). Keyed encounter|mover|target. */
const OVERRUN_PAIR_WINDOW_MS = 7000;
const recentPairs = new Map<string, number>();
const offered = new Set<string>();
const pairKey = (encounterId: string, moverId: string, targetId: string): string => `${upper(encounterId)}|${upper(moverId)}|${upper(targetId)}`;
const flights = new Map<string, Promise<unknown>>();
async function prefetch(encounter: Row): Promise<void> {
  const fetch = (id: string): Promise<unknown> => {
    if (definition(encounter, id)) return Promise.resolve(null);
    let p = flights.get(id);
    if (!p) { p = fetchAndCacheObject(id).catch(() => null).finally(() => flights.delete(id)); flights.set(id, p); }
    return p;
  };
  await Promise.all([FULL_OVERRUN, OVERRUN_FORTUNATE].map(fetch));
  const full = definition(encounter, FULL_OVERRUN);
  if (!full) return;
  const skills = ['skill_roll_contester', 'skill_roll_contestee'].flatMap(key => ftLayerEntries(full, key) ?? []).map(e =>
    upper(typeof e === 'string' ? e.split(/[|\s]/)[0] : row(e)?.item_id ?? row(row(e)?.items)?.item_id));
  await Promise.all([...new Set(skills)].filter(id => UUID.test(id)).map(fetch));
}
registerOverrunWalkPlanner({ plan(encounterId, actorId, triggers, inCombat) {
  const encounter = actorEncounterFor(encounterId) as Row | null;
  const mover = actorRowFor(encounterId, actorId) as Row | null;
  if (!encounter || !mover) return { kind: 'handback', reason: 'overrun:not-held' };
  const needed = new Set([FULL_OVERRUN, OVERRUN_FORTUNATE]);
  const full = definition(encounter, FULL_OVERRUN);
  if (full) for (const key of ['skill_roll_contester', 'skill_roll_contestee']) for (const e of ftLayerEntries(full, key) ?? [])
    needed.add(upper(typeof e === 'string' ? e.split(/[|\s]/)[0] : row(e)?.item_id ?? row(row(e)?.items)?.item_id));
  if ([...needed].some(id => !definition(encounter, id))) { void prefetch(encounter); return { kind: 'handback', reason: 'overrun:definition-not-held' }; }
  const members = new Map<string, Row>();
  for (const r of Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []) {
    const member = row(r), id = upper(row(member?.encounter_creatures)?.encounter_creature_id);
    if (member && id) members.set(id, member);
  }
  const parties = new Set([upper(actorId), ...triggers.flatMap(t => t.ids.map(upper))]);
  for (const id of parties) {
    const member = members.get(id);
    if (!row(row(member?.computed)?.creature) || !row(member?.item_layer_types)) return { kind: 'handback', reason: 'overrun:party-compile-not-held' };
  }
  const now = Date.now();
  const cooled = (targetId: string): boolean => now - (recentPairs.get(pairKey(encounterId, actorId, targetId)) ?? -Infinity) < OVERRUN_PAIR_WINDOW_MS;
  return resolveOverrunContest({ encounter, members, actorId, triggers, inCombat, cooled,
    read: { ...abilityTruthReaders(mover, encounter, parties), triggerConsent: createTriggerConsent(encounterId) } as never }, browserRollSource());
}, recorded(encounterId, actorId, evidence, operationId) {
  const now = Date.now();
  for (const r of evidence.verdict.rows) if (r.roll_type_1 === CONTEST_ROLL_ID && upper(r.roll_by) !== upper(actorId)) recentPairs.set(pairKey(encounterId, actorId, String(r.roll_by)), now);
  for (const [key, at] of recentPairs) if (now - at >= OVERRUN_PAIR_WINDOW_MS) recentPairs.delete(key);
  if (evidence.fortunateOffers?.length && !offered.has(operationId)) {
    offered.add(operationId);
    if (offered.size > 256) offered.delete(offered.values().next().value!);
    offerFortunate(encounterId, actorId, evidence.fortunateOffers);
  }
} });
const sweep = (): void => { for (const e of window.FateTide?.encounterCache?.values() ?? []) { const r = row(e); if (r) void prefetch(r); } };
for (const name of ['ft:encounter-state-received', 'ft:encounter-installed', 'ft:encounter-rows-patched'])
  window.addEventListener(name, e => { const enc = row((e as CustomEvent).detail?.encounterArr); if (enc) void prefetch(enc); else sweep(); });
sweep();
setTimeout(sweep, 2000);
setTimeout(sweep, 8000);
